import { useQuery } from '@tanstack/react-query';
import { formatarReais } from '@reducee/contracts';

import { chamarApi } from '../api/cliente.ts';
import { useSessao } from '../autenticacao/sessao.tsx';
import { CabecalhoDePagina, Cartao, Celula, Indicador, Linha, LinhaDeLista, Tabela, data, } from '../componentes/Superficie.tsx';

type Painel = {
  meta: {
    id: string;
    nome: string;
    dataInicial: string;
    dataFinal: string;
    valorCentavos: number;
    realizadoCentavos: number;
    percentual: number;
  } | null;
  ciclos: Array<{
    id: string;
    ordem: number;
    nome: string;
    dataInicial: string;
    dataFinal: string;
    metaCentavos: number;
    realizadoCentavos: number;
    atual: boolean;
  }>;
  faixas: Array<{ valorDe: number; valorAte: number | null; percentual: number }>;
  producao: Array<{
    usuaria: { id: string; nome: string };
    fechadaCentavos: number;
    pagaCentavos: number;
    percentualBonificacao: number;
    bonificacaoCentavos: number;
  }>;
  competencia: string;
};

/**
 * Metas e bonificação.
 *
 * A tela insiste em separar duas colunas que a operação costuma confundir:
 * Produção fechada mede esforço comercial, Produção paga é o que a operadora
 * confirmou — e só a segunda bonifica.
 */
export function PaginaMetas(): React.JSX.Element {
  const { usuaria } = useSessao();
  const consulta = useQuery({
    queryKey: ['metas'],
    queryFn: () => chamarApi<Painel>('/metas'),
  });

  const painel = consulta.data;
  const ehAdministradora = usuaria?.papel === 'ADMINISTRADORA';

  return (
    <section>
      <CabecalhoDePagina
        area="Performance"
        titulo="Metas & Bonificação"
        descricao="Campanhas editáveis e bonificação por produção paga no mês."
      />
      <p className="mb-5 text-[13px] text-texto-suave">{ehAdministradora
          ? 'Andamento da campanha e bonificação da equipe no mês.'
          : 'Seu andamento na campanha e sua bonificação no mês.'}</p>

      {painel?.meta ? (
        <Cartao
          className="mt-6"
          titulo={painel.meta.nome}
          descricao={`${data(painel.meta.dataInicial)} → ${data(painel.meta.dataFinal)}`}
        >
          <div className="flex items-baseline justify-between text-sm">
            <span className="text-texto-suave">
              {formatarReais(painel.meta.realizadoCentavos)} de{' '}
              {formatarReais(painel.meta.valorCentavos)}
            </span>
            <span className="font-semibold">
              {painel.meta.percentual.toFixed(2).replace('.', ',')}%
            </span>
          </div>
          <div className="mt-2 h-2 overflow-hidden rounded-full bg-marinho-suave">
            <div
              className="h-full rounded-full bg-dourado"
              style={{ width: `${Math.min(100, painel.meta.percentual)}%` }}
            />
          </div>

          <div className="mt-4 grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
            {painel.ciclos.map((ciclo) => (
              <div
                key={ciclo.id}
                className={`rounded-lg border p-3 text-xs ${
                  ciclo.atual ? 'border-marinho-claro bg-marinho-suave' : 'border-linha'
                }`}
              >
                <p className="font-semibold text-marinho">
                  {ciclo.nome}
                  {ciclo.atual ? ' · atual' : ''}
                </p>
                <p className="text-texto-suave">
                  {data(ciclo.dataInicial)} – {data(ciclo.dataFinal)}
                </p>
                <p className="mt-1 font-medium">
                  {formatarReais(ciclo.realizadoCentavos)} / {formatarReais(ciclo.metaCentavos)}
                </p>
              </div>
            ))}
          </div>
        </Cartao>
      ) : (
        <p className="mt-6 rounded-xl bg-superficie p-8 text-center text-texto-suave ring-1 ring-linha">
          {consulta.isLoading ? 'Carregando…' : 'Nenhuma campanha ativa no momento.'}
        </p>
      )}

      <div className="mt-6 grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
        <Indicador
          teste="producao-paga-mes"
          rotulo="Produção paga no mês"
          valor={formatarReais(
            (painel?.producao ?? []).reduce((total, linha) => total + linha.pagaCentavos, 0),
          )}
          detalhe={`Competência ${painel?.competencia ?? '—'}`}
        />
        <Indicador
          rotulo="Produção fechada no mês"
          valor={formatarReais(
            (painel?.producao ?? []).reduce((total, linha) => total + linha.fechadaCentavos, 0),
          )}
          detalhe="Mede esforço comercial; não bonifica"
        />
        <Indicador
          teste="bonificacao-apurada"
          rotulo="Bonificação apurada"
          valor={formatarReais(
            (painel?.producao ?? []).reduce((total, linha) => total + linha.bonificacaoCentavos, 0),
          )}
          detalhe="Soma das faixas individuais, não do total da equipe"
          tom="sucesso"
        />
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-2">
        <Cartao titulo="Faixas de bonificação" descricao="Aplicadas sobre o total de vendas fechadas com 1º boleto pago no mês.">
          {(painel?.faixas ?? []).map((faixa) => (
            <LinhaDeLista
              key={faixa.valorDe}
              titulo={
                faixa.valorAte === null
                  ? `A partir de ${formatarReais(faixa.valorDe)}`
                  : `${formatarReais(faixa.valorDe)} a ${formatarReais(faixa.valorAte)}`
              }
              valor={`${faixa.percentual}%`}
            />
          ))}
        </Cartao>

        <Cartao titulo="Produção válida por responsável" descricao="Competência definida pela data de pagamento do 1º boleto.">
          <Tabela
            colunas={['Pessoa', 'Fechada', 'Paga', 'Faixa', 'Bonificação']}
            vazio="Sem produção neste mês."
            carregando={consulta.isLoading}
          >
            {(painel?.producao ?? []).map((linha) => (
              <Linha key={linha.usuaria.id}>
                <Celula className="font-medium">{linha.usuaria.nome}</Celula>
                <Celula>{formatarReais(linha.fechadaCentavos)}</Celula>
                <Celula>{formatarReais(linha.pagaCentavos)}</Celula>
                <Celula>{linha.percentualBonificacao}%</Celula>
                <Celula className="font-semibold">
                  {formatarReais(linha.bonificacaoCentavos)}
                </Celula>
              </Linha>
            ))}
          </Tabela>
        </Cartao>
      </div>
    </section>
  );
}
